/**
 * ZoneShapes — the pass-2 vocabulary extended to the things that OWN ground
 * or stand on it for a while: GOOGL's three zones, the discharge column, the
 * thruster exhaust. Same rules as CelShapes: node materials, every
 * per-instance value a uniform, shared geometry, pooled and prewarmed.
 *
 *   column    a vertical cel flame / energy column sprite (chain discharge,
 *             fault-line eruptions, and flipped, SPCX's hover exhaust)
 *   vortex    PageRank: spiral arms turning INTO a dark well on the floor
 *   runeTrap  Tracking Pixel: a glyph disc with an armed / unarmed read
 *   nullDome  Deindexed's boundary: a faceted lattice dome, ink at the rim
 *   nullFloor Deindexed's interior: a darkened, hatched disc with a scan
 */

import * as THREE from 'three/webgpu';
import { Fn, uv, float, vec2, smoothstep, step, uniform, mix, atan, fract, time, sin, log, normalView, positionLocal, min } from 'three/tsl';
import { cloud, erode } from './CelLook.js';
import { acquireShape, commonUniforms, ramp, band, baseMaterial, circleGeo } from './CelShapes.js';

let _domeGeo = null;
function domeGeo() {
  if (!_domeGeo) _domeGeo = new THREE.SphereGeometry(1, 28, 12, 0, Math.PI * 2, 0, Math.PI / 2);
  return _domeGeo;
}

/**
 * The column: a teardrop flame along +y (pointed at the top, round at the
 * base), a white core up the middle, hot, body, ink rim, dark vertical streaks
 * scrolling up, and a flicker on the width. A Sprite so it keeps its area from
 * the 55° camera; `spin` = π turns it into a downward exhaust.
 */
function createColumn(colorHex) {
  const mat = new THREE.SpriteNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  const u = commonUniforms(colorHex);
  const spin = uniform(0);
  mat.rotationNode = spin;
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const t = local.y.add(1).mul(0.5); // 0 base → 1 tip
  const flicker = float(1).add(sin(time.mul(23).add(u.seed).add(t.mul(9))).mul(0.1));
  const w = sin(t.mul(Math.PI)).pow(0.7).mul(float(1).sub(t.mul(0.35))).mul(0.62).mul(flicker);
  const q = local.x.abs().div(w.max(0.02));
  const streak = step(float(0.72), fract(t.mul(5).sub(time.mul(2.4)).add(u.seed).add(local.x.mul(0.6))));
  const n = cloud(local.mul(vec2(2.2, 1.6)), u.seed.mul(8));
  // Erodes from the tip down.
  const prog = u.erode.mul(1.15).add(t.mul(0.35).mul(u.erode)).clamp(0, 1);
  const e = erode(n, prog, { edge: 0.05, front: 0.13 });

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, smoothstep(float(0.9), float(0.84), q));
    col = mix(col, c.deep, streak.mul(smoothstep(float(0.9), float(0.84), q)).mul(0.6));
    col = mix(col, c.hot, smoothstep(float(0.58), float(0.5), q));
    // A thin spine of white-hot core in the lower two thirds only: the core is
    // what blooms, and a wide, tall one turned three columns into three
    // halos that filled a phone screen (the medium tier's bloom).
    col = mix(col, c.core, smoothstep(float(0.17), float(0.1), q).mul(smoothstep(float(0.72), float(0.45), t)));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => smoothstep(float(1.0), float(0.94), q).mul(e.alive).mul(u.fade))();

  const sprite = new THREE.Sprite(mat);
  sprite.frustumCulled = false;
  return { mesh: sprite, u: { ...u, spin } };
}

/**
 * The gravity well: three spiral arms winding INTO the centre (they turn with
 * `time`, so the pull is visible while it stands still), a dark ink well in
 * the middle, an outer ink rim with a keyline at the true radius. Floor
 * colour. Unit disc; the caller scales it to the radius and erodes it out.
 */
function createVortex(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const a01 = atan(local.x, local.y).mul(1 / (Math.PI * 2)).add(0.5);
  // log(r) makes the arms tighten toward the centre — the classic spiral.
  const s = fract(a01.mul(3).add(log(r.max(0.03)).mul(1.35)).add(time.mul(1.6)).add(u.seed));
  const arm = band(s, 0.3, 0.7, 0.05).mul(smoothstep(float(0.12), float(0.3), r)).mul(smoothstep(float(0.97), float(0.86), r));
  const well = smoothstep(float(0.26), float(0.1), r);
  const rim = band(r, 0.9, 1.0, 0.015);
  const n = cloud(local.mul(3), u.seed.mul(6));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.12 });

  mat.colorNode = Fn(() => {
    let col = c.deep;
    col = mix(col, c.body, arm);
    col = mix(col, c.hot, arm.mul(band(s, 0.47, 0.53, 0.03)));
    col = mix(col, c.ink, well);
    col = mix(col, c.ink, rim);
    col = mix(col, c.core, band(r, 0.93, 0.955, 0.012));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => {
    const base = float(0.2).mul(smoothstep(float(1.0), float(0.97), r));
    const shape = base.add(arm.mul(0.75)).add(well.mul(0.85)).add(rim.mul(0.9)).min(1);
    return shape.mul(e.alive).mul(u.fade);
  })();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

/**
 * The trap rune. An outer band with rotating ticks and a glyph inside: a hex,
 * three spokes, a centre dot. `armed` 0 → the glyph is a dim deep-toned
 * outline (placed, not yet live); 1 → filled, with a keyline that pulses. It
 * erodes out when it fires. Floor colour; unit disc scaled to the radius.
 */
function createRuneTrap(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const armed = uniform(0);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const a01 = atan(local.x, local.y).mul(1 / (Math.PI * 2)).add(0.5);
  const ticks = step(float(0.5), fract(a01.mul(12).add(time.mul(0.1)).add(u.seed)));
  const outer = band(r, 0.86, 1.0, 0.015);
  const tickBand = band(r, 0.74, 0.82, 0.015).mul(ticks);
  const hexD = local.y.abs().max(local.x.abs().mul(0.866).add(local.y.abs().mul(0.5)));
  const hexLine = band(hexD, 0.44, 0.52, 0.02);
  const spoke = smoothstep(float(0.05), float(0.03), fract(a01.mul(3).add(0.5)).sub(0.5).abs()).mul(smoothstep(float(0.14), float(0.2), r)).mul(smoothstep(float(0.5), float(0.42), r));
  const dot = smoothstep(float(0.14), float(0.1), r);
  const glyph = hexLine.max(spoke).max(dot);
  const pulse = float(0.55).add(sin(time.mul(5).add(u.seed)).mul(0.45)).mul(armed);
  const n = cloud(local.mul(3.5), u.seed.mul(5));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.12 });

  mat.colorNode = Fn(() => {
    let col = c.ink;
    col = mix(col, c.body, band(r, 0.885, 0.975, 0.015));
    col = mix(col, c.core, band(r, 0.92, 0.94, 0.01).mul(float(0.4).add(armed.mul(0.6))));
    col = mix(col, c.body, tickBand);
    col = mix(col, mix(c.deep, c.body, armed), glyph);
    col = mix(col, c.hot, glyph.mul(pulse));
    col = mix(col, c.core, dot.mul(armed));
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => {
    const fill = smoothstep(float(0.86), float(0.8), r).mul(float(0.06).add(armed.mul(0.1)));
    const g = glyph.mul(float(0.38).add(armed.mul(0.62)));
    return outer.add(tickBand.mul(0.8)).add(g).add(fill).min(1).mul(e.alive).mul(u.fade);
  })();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, armed } };
}

/**
 * Deindexed's boundary: a hemisphere whose silhouette is INK (edge-on faces),
 * with a triangle lattice drawn over it in the body colour, densest at the
 * base, a hot band just above the deck, and a faint interior so you can look
 * into it. A boundary you can read, not a milky cup. Erodes out with a front.
 */
function createNullDome(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const facing = normalView.z.abs();
  const rim = smoothstep(float(0.5), float(0.12), facing);
  const h = positionLocal.y.clamp(0, 1); // 0 at the deck → 1 at the pole
  const g = uv().mul(vec2(18, 8));
  const l1 = fract(g.x).sub(0.5).abs();
  const l2 = fract(g.y.add(g.x.mul(0.5))).sub(0.5).abs();
  const l3 = fract(g.y.sub(g.x.mul(0.5))).sub(0.5).abs();
  const lines = smoothstep(float(0.09), float(0.04), min(l1, min(l2, l3))).mul(smoothstep(float(0.72), float(0.12), h));
  const scan = band(fract(h.mul(1.4).sub(time.mul(0.35))), 0.0, 0.06, 0.02);
  const foot = band(h, 0.0, 0.05, 0.01);
  const n = cloud(uv().mul(vec2(6, 3)), u.seed.mul(4));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.12 });

  mat.colorNode = Fn(() => {
    let col = c.deep;
    col = mix(col, c.body, lines);
    col = mix(col, c.hot, scan.mul(0.7));
    col = mix(col, c.ink, smoothstep(float(0.45), float(0.75), rim));
    col = mix(col, c.hot, smoothstep(float(0.2), float(0.45), rim).mul(smoothstep(float(0.75), float(0.45), rim)));
    col = mix(col, c.core, foot);
    col = mix(col, c.core, e.front);
    return col;
  })();
  // Light-handed: the dome is a boundary to look INTO. The lattice and the
  // interior tint together must stay under what the bodies inside need.
  mat.opacityNode = Fn(() => {
    const interior = float(0.025).add(lines.mul(0.16)).add(scan.mul(0.08));
    return interior.add(rim.mul(0.85)).add(foot.mul(0.9)).min(1).mul(e.alive).mul(u.fade);
  })();

  const mesh = new THREE.Mesh(domeGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

/**
 * Deindexed's interior: the deck inside goes DARK and hatched (nothing works
 * here), with a scan ring sweeping inward. The boundary band is `zoneRing`.
 */
function createNullFloor(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const c = ramp(u);

  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const hatch = step(float(0.55), fract(local.x.add(local.y).mul(6).add(time.mul(0.3)).add(u.seed)));
  const scanR = float(1).sub(fract(time.mul(0.3).add(u.seed)));
  const scan = band(r.sub(scanR), -0.03, 0.0, 0.01);
  const inside = smoothstep(float(1.0), float(0.97), r);
  const n = cloud(local.mul(3), u.seed.mul(7));
  const e = erode(n, u.erode, { edge: 0.05, front: 0.1 });

  mat.colorNode = Fn(() => {
    let col = mix(c.ink, c.deep, 0.35);
    col = mix(col, c.deep, hatch.mul(0.5));
    col = mix(col, c.hot, scan);
    col = mix(col, c.core, e.front);
    return col;
  })();
  mat.opacityNode = Fn(() => float(0.13).add(hatch.mul(0.07)).add(scan.mul(0.45)).mul(inside).mul(e.alive).mul(u.fade))();

  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u };
}

export function column(colorHex, { depthTest = true } = {}) {
  const r = acquireShape('column', () => createColumn(colorHex), colorHex, { depthTest });
  r.u.spin.value = 0;
  return r;
}
export function vortex(colorHex) {
  return acquireShape('vortex', () => createVortex(colorHex), colorHex, { floor: true });
}
export function runeTrap(colorHex) {
  return acquireShape('runeTrap', () => createRuneTrap(colorHex), colorHex, { floor: true });
}
export function nullDome(colorHex) {
  return acquireShape('nullDome', () => createNullDome(colorHex), colorHex, { floor: true });
}
export function nullFloor(colorHex) {
  return acquireShape('nullFloor', () => createNullFloor(colorHex), colorHex, { floor: true });
}

/** Every pooled key this module can produce, for the prewarm and its test. */
export const ZONE_SHAPE_KEYS = ['column', 'vortex', 'runeTrap', 'nullDome', 'nullFloor'];
